import { useState, useMemo, useContext } from "react";
import {
  Box, Button, Chip, Divider, Input, Slider, Stack, Switch, Typography,
} from "@mui/joy";
import { motion, AnimatePresence } from "framer-motion";
import FunctionsRoundedIcon from "@mui/icons-material/FunctionsRounded";
import TimerRoundedIcon from "@mui/icons-material/TimerRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";

import { Section, Container, COLORS, GlowOrbs, formatPrice } from "../shared/ui";
import { calculatePrice, OBJECT_TYPES, PROGRAMS } from "../shared/pricing";
import { LayoutContext } from "../context";
import { useEffect } from "react"; 
import { APIClientCalculatorQuote } from "../api";


export default function PriceCalculator({ embedded = false, onClose }) {
  const { setShowOrder, setEditOrder, setModalAuth, setShowQuickOrder, setQuickOrderCalcAmount } = useContext(LayoutContext);

  // ─── State ──
  const [type, setType] = useState("Квартира");
  const [area, setArea] = useState(80);
  const [program, setProgram] = useState("AutoCAD");
  const [outOfCityKm, setOutOfCityKm] = useState(0);
  const [electricity, setElectricity] = useState(false);
  const [outsideFacadeM, setOutsideFacadeM] = useState(0);
  const [insideFacadeM, setInsideFacadeM] = useState(0);
  const [urgent, setUrgent] = useState(false);
  const [auditArea, setAuditArea] = useState(0);
  const [keyDelivery, setKeyDelivery] = useState(false);
  const [winter, setWinter] = useState(false);
  const [extrasOpen, setExtrasOpen] = useState(false);

  const [serverRate, setServerRate] = useState(null);
  const [serverMin, setServerMin]   = useState(null);

  const result = useMemo(
    () => calculatePrice({
      area, outOfCityKm, electricity, outsideFacadeM, insideFacadeM,
      urgent, auditArea, keyDelivery, winter,
      baseRate: serverRate ?? undefined,
      minCallout: serverMin ?? undefined,
    }),
    [area, outOfCityKm, electricity, outsideFacadeM, insideFacadeM, urgent, auditArea, keyDelivery, winter, serverRate, serverMin]
  );

  const handleQuickOrder = () => {
    setQuickOrderCalcAmount(result.total);
    setShowQuickOrder(true);
    if (embedded) onClose?.();
  };

  const handleOrder = () => {
    setEditOrder({
      type,
      area,
      program,
      estimatedPrice: result.total,
      extras: { electricity, outsideFacadeM, insideFacadeM, urgent, auditArea, keyDelivery, winter, outOfCityKm },
    });
    setShowOrder(true);
    if (embedded) onClose?.();
  };

  useEffect(() => {
    let cancelled = false;
    APIClientCalculatorQuote({ square: 100 }).then(res => {
      if (cancelled || !res || res.status === "error") return;
      const b = res.breakdown || {};
      if (typeof b.rate_per_sqm === "number") setServerRate(b.rate_per_sqm);
      if (typeof b.minimum === "number")      setServerMin(b.minimum);
    });
    return () => { cancelled = true; };
  }, []);

  const inner = (
      <Container maxWidth={embedded ? "100%" : 1180}>
        {/* Шапка секции — только на лендинге */}
        {!embedded && (
        <Box sx={{ textAlign: "center", mb: { xs: 5, md: 7 } }}>
          <Chip
            variant="outlined"
            size="sm"
            sx={{
              mb: 2,
              fontWeight: 700,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              fontSize: "0.72rem",
              borderColor: "rgba(99,102,241,0.4)",
              background: "rgba(99,102,241,0.12)",
              color: COLORS.brandLight,
            }}
          >
            Калькулятор стоимости
          </Chip>
          <Typography
            level="h2"
            sx={{
              color: "#fff",
              fontWeight: 900,
              fontSize: { xs: "2rem", md: "3rem" },
              letterSpacing: "-1.5px",
              lineHeight: 1.1,
              mb: 2,
            }}
          >
            Точная цена за{" "}
            <Box component="span" sx={{ background: "linear-gradient(135deg, #818cf8, #c084fc)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
              30 секунд
            </Box>
          </Typography>
          <Typography level="body-lg" sx={{ color: COLORS.textDim, maxWidth: 580, mx: "auto" }}>
            Цена зафиксирована в договоре и не изменится после выезда — гарантируем.
          </Typography>
        </Box>
        )}

        {/* Тело калькулятора */}
        <Box
          sx={{
            position: "relative",
            borderRadius: "28px",
            background: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.1)",
            backdropFilter: "blur(20px)",
            p: { xs: 3, md: 5 },
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1.2fr 1fr" },
            gap: { xs: 4, md: 5 },
          }}
        >
          {/* ─── Левая часть: контролы ─── */}
          <Box>
            {/* Тип объекта */}
            <FieldLabel icon={<FunctionsRoundedIcon sx={{ fontSize: 16 }} />}>Тип объекта</FieldLabel>
            <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 3.5 }}>
              {OBJECT_TYPES.map((t) => (
                <ChipButton key={t} active={type === t} onClick={() => setType(t)}>
                  {t}
                </ChipButton>
              ))}
            </Stack>

            {/* Площадь — целочисленный ввод (пункт 5) */}
            <FieldLabel hint="от 20 до 2000">Площадь объекта, м²</FieldLabel>
            <Input
              type="number"
              value={area}
              onChange={(e) => {
                const raw = e.target.value;
                setArea(raw === "" ? "" : Math.max(0, parseInt(raw, 10) || 0));
              }}
              onBlur={() => setArea((a) => Math.min(2000, Math.max(20, parseInt(a, 10) || 20)))}
              placeholder="Например, 80"
              slotProps={{ input: { min: 20, max: 2000, inputMode: "numeric" } }}
              endDecorator={<Typography level="body-sm" sx={{ color: COLORS.textMute }}>м²</Typography>}
              sx={{
                mb: 3.5,
                background: "rgba(255,255,255,0.05)",
                borderColor: "rgba(255,255,255,0.14)",
                color: "#fff",
                fontFamily: "monospace",
                fontSize: "1.1rem",
                "&:hover": { background: "rgba(255,255,255,0.08)" },
                "&::before": { display: "none" },
              }}
            />

            {/* Программа */}
            <FieldLabel>Программа для чертежа</FieldLabel>
            <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 3.5 }}>
              {PROGRAMS.map((p) => (
                <ChipButton key={p} active={program === p} onClick={() => setProgram(p)}>
                  {p}
                </ChipButton>
              ))}
            </Stack>

            {/* Выезд за город */}
            <FieldLabel hint="Бесплатно в черте города">Расстояние за город, км</FieldLabel>
            <Input
              type="number"
              value={outOfCityKm || ""}
              onChange={(e) => setOutOfCityKm(Math.max(0, parseInt(e.target.value) || 0))}
              placeholder="0 — в черте города"
              slotProps={{ input: { min: 0, max: 500 } }}
              sx={{
                mb: 3.5,
                background: "rgba(255,255,255,0.05)",
                borderColor: "rgba(255,255,255,0.14)",
                color: "#fff",
                fontFamily: "monospace",
                "&:hover": { background: "rgba(255,255,255,0.08)" },
                "&::before": { display: "none" },
              }}
            />

            {/* Аккордеон доп. опций */}
            <Box
              onClick={() => setExtrasOpen(!extrasOpen)}
              sx={{
                cursor: "pointer",
                p: 2,
                borderRadius: "12px",
                border: "1px solid rgba(99,102,241,0.3)",
                background: extrasOpen ? "rgba(99,102,241,0.08)" : "transparent",
                transition: "all 0.2s",
                "&:hover": { background: "rgba(99,102,241,0.06)" },
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" spacing={1} alignItems="center">
                  <TuneRoundedIcon sx={{ color: COLORS.brandLight, fontSize: 18 }} />
                  <Typography sx={{ color: "#fff", fontWeight: 600, fontSize: "0.9rem" }}>
                    Дополнительные услуги
                  </Typography>
                </Stack>
                <motion.div animate={{ rotate: extrasOpen ? 180 : 0 }}>
                  <Typography sx={{ color: COLORS.brandLight, fontSize: "1.2rem" }}>▾</Typography>
                </motion.div>
              </Stack>
            </Box>

            <AnimatePresence>
              {extrasOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0, marginTop: 0 }}
                  animate={{ height: "auto", opacity: 1, marginTop: 16 }}
                  exit={{ height: 0, opacity: 0, marginTop: 0 }}
                  transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                  style={{ overflow: "hidden" }}
                >
                  <Stack spacing={2}>
                    <ToggleOption
                      label="Замер электрики"
                      hint="+25 ₽/м² — розетки, выключатели, выводы"
                      value={electricity}
                      onChange={setElectricity}
                    />

                    <NumberOption
                      label="Уличный фасад, м"
                      hint="+2 500 ₽ за каждые 10 м по ширине"
                      value={outsideFacadeM}
                      onChange={setOutsideFacadeM}
                      placeholder="0"
                    />

                    <NumberOption
                      label="Внутренний фасад (расстекловки), м"
                      hint="+2 000 ₽ за каждые 10 м по периметру"
                      value={insideFacadeM}
                      onChange={setInsideFacadeM}
                      placeholder="0"
                    />

                    <ToggleOption
                      label="Срочный выезд (в течение 1 суток)"
                      hint="+3 000 ₽"
                      value={urgent}
                      onChange={setUrgent}
                    />

                    <NumberOption
                      label="Аудит объекта, м²"
                      hint="+3 500 ₽ за каждые 100 м² — проверка на соответствие ТЗ"
                      value={auditArea}
                      onChange={setAuditArea}
                      placeholder="0"
                    />

                    <ToggleOption
                      label="Доставка ключей"
                      hint="+3 000 ₽ — забрать и вернуть"
                      value={keyDelivery}
                      onChange={setKeyDelivery}
                    />

                    <ToggleOption
                      label="Зимний сезон (без отопления)"
                      hint="коэффициент ×1.2 к итогу"
                      value={winter}
                      onChange={setWinter}
                    />
                  </Stack>
                </motion.div>
              )}
            </AnimatePresence>
          </Box>

          {/* ─── Правая часть: breakdown + итог ─── */}
          <Box
            sx={{
              position: { md: "sticky" },
              top: { md: 100 },
              alignSelf: "flex-start",
              background: "linear-gradient(160deg, rgba(99,102,241,0.1), rgba(139,92,246,0.05))",
              border: "1px solid rgba(139,92,246,0.25)",
              borderRadius: "20px",
              p: { xs: 3, md: 3.5 },
              display: "flex",
              flexDirection: "column",
              minHeight: 400,
            }}
          >
            <Typography
              level="body-xs"
              sx={{
                color: COLORS.textMute,
                textTransform: "uppercase",
                letterSpacing: "0.14em",
                fontWeight: 700,
                mb: 1.5,
              }}
            >
              Расчёт стоимости
            </Typography>

            {/* Breakdown с анимацией появления строк */}
            <Box sx={{ mb: 2, flex: 1 }}>
              <AnimatePresence mode="popLayout">
                {result.breakdown.map((item) => (
                  <motion.div
                    key={item.label}
                    layout
                    initial={{ opacity: 0, x: -16, height: 0 }}
                    animate={{ opacity: 1, x: 0, height: "auto" }}
                    exit={{ opacity: 0, x: 16, height: 0 }}
                    transition={{ duration: 0.32, ease: [0.4, 0, 0.2, 1] }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      sx={{ py: 1, borderBottom: "1px dashed rgba(255,255,255,0.08)" }}
                    >
                      <Box sx={{ pr: 2 }}>
                        <Typography
                          level="body-sm"
                          sx={{ color: "rgba(255,255,255,0.85)", fontWeight: 500, lineHeight: 1.4 }}
                        >
                          {item.label}
                        </Typography>
                        {item.hint && (
                          <Typography level="body-xs" sx={{ color: "#34d399", mt: 0.25, fontSize: "0.7rem" }}>
                            {item.hint}
                          </Typography>
                        )}
                      </Box>
                      <Typography
                        sx={{
                          color: "#fff",
                          fontWeight: 700,
                          fontFamily: "monospace",
                          whiteSpace: "nowrap",
                          fontSize: "0.9rem",
                        }}
                      >
                        {formatPrice(item.amount)}
                      </Typography>
                    </Stack>
                  </motion.div>
                ))}
              </AnimatePresence>

              {result.minApplied && (
                <Typography
                  level="body-xs"
                  sx={{ color: "#fbbf24", mt: 1.5, fontSize: "0.72rem", lineHeight: 1.5 }}
                >
                  * Применена минимальная стоимость выезда — 7 000 ₽
                </Typography>
              )}
            </Box>

            <Divider sx={{ borderColor: "rgba(255,255,255,0.1)", mb: 2 }} />

            {/* Итого */}
            <Box sx={{ mb: 2.5 }}>
              <Typography
                level="body-xs"
                sx={{ color: COLORS.textMute, textTransform: "uppercase", letterSpacing: "0.12em", fontWeight: 700, mb: 0.5 }}
              >
                Итого
              </Typography>
              <motion.div
                key={result.total}
                initial={{ scale: 1.05, opacity: 0.6 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.35 }}
              >
                <Typography
                  sx={{
                    fontWeight: 900,
                    fontFamily: "monospace",
                    fontSize: { xs: "2.4rem", md: "2.8rem" },
                    letterSpacing: "-1.5px",
                    lineHeight: 1,
                    background: "linear-gradient(135deg, #a5b4fc, #c084fc)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  {formatPrice(result.total)}
                </Typography>
              </motion.div>
            </Box>

            {/* Мета-информация */}
            <Stack spacing={1} sx={{ mb: 3 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <TimerRoundedIcon sx={{ fontSize: 16, color: COLORS.brandLight }} />
                <Typography level="body-xs" sx={{ color: COLORS.textDim }}>
                  Готово за{" "}
                  <Box component="span" sx={{ color: COLORS.brandLight, fontWeight: 700 }}>
                    {result.days} {result.days === 1 ? "день" : result.days < 5 ? "дня" : "дней"}
                  </Box>
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <LockRoundedIcon sx={{ fontSize: 16, color: COLORS.brandLight }} />
                <Typography level="body-xs" sx={{ color: COLORS.textDim }}>
                  Цена зафиксирована в договоре
                </Typography>
              </Stack>
            </Stack>

            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Button
                fullWidth
                size="lg"
                // onClick={handleOrder}
                onClick={handleQuickOrder}
                sx={{
                  fontWeight: 800,
                  py: 1.5,
                  borderRadius: "14px",
                  background: `linear-gradient(135deg, ${COLORS.brand}, ${COLORS.brand2})`,
                  boxShadow: "0 8px 28px rgba(99,102,241,0.5)",
                  "&:hover": {
                    boxShadow: "0 12px 40px rgba(99,102,241,0.7)",
                    background: `linear-gradient(135deg, ${COLORS.brand}, ${COLORS.brand2})`,
                  },
                }}
              >
                Оставить заявку
              </Button>
              {/* Заказать по этой цене */}
            </motion.div>
          </Box>
        </Box>
      </Container>
  );

  return embedded ? (
    <Box sx={{ background: `linear-gradient(180deg, ${COLORS.dark} 0%, ${COLORS.darkMid} 100%)`, borderRadius: "20px", p: { xs: 2, md: 3 } }}>
      {inner}
    </Box>
  ) : (
    <Section
      id="calculator"
      dark
      py={{ xs: 10, md: 14 }}
      sx={{ background: `linear-gradient(180deg, ${COLORS.dark} 0%, ${COLORS.darkMid} 100%)` }}
    >
      <GlowOrbs />
      {inner}
    </Section>
  );
}

// ─── Вспомогательные компоненты ───────────────────────────────────────────

function FieldLabel({ children, icon, hint }) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
      {icon && <Box sx={{ color: COLORS.brandLight, display: "flex" }}>{icon}</Box>}
      <Typography
        level="body-sm"
        sx={{
          color: "rgba(255,255,255,0.75)",
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          fontSize: "0.72rem",
        }}
      >
        {children}
      </Typography>
      {hint && (
        <Typography level="body-xs" sx={{ color: COLORS.textMute, fontWeight: 400, textTransform: "none", ml: 0.5 }}>
          · {hint}
        </Typography>
      )}
    </Stack>
  );
}

function ChipButton({ active, onClick, children }) {
  return (
    <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}>
      <Chip
        color="primary"
        variant={active ? "solid" : "outlined"}
        onClick={onClick}
        sx={{
          cursor: "pointer",
          fontWeight: active ? 700 : 500,
          fontSize: "0.85rem",
          "--Chip-paddingInline": "14px",
          "--Chip-minHeight": "34px",
          '& button': {
            backgroundColor: active ? "soft" : "transparent",
            border: '1px solid #8d8eef',
          },
          '& button:hover': {
            backgroundColor: "#8a8bcf9e",
          },
          '& span': {
            color: "white",
          }
        }}
      >
        {children}
      </Chip>
    </motion.div>
  );
}

function ToggleOption({ label, hint, value, onChange }) {
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      spacing={2}
      sx={{
        p: 1.5,
        borderRadius: "10px",
        background: value ? "rgba(99,102,241,0.08)" : "rgba(255,255,255,0.02)",
        border: `1px solid ${value ? "rgba(99,102,241,0.3)" : "rgba(255,255,255,0.06)"}`,
        transition: "all 0.2s",
      }}
    >
      <Box>
        <Typography level="body-sm" sx={{ color: "#fff", fontWeight: 600 }}>
          {label}
        </Typography>
        <Typography level="body-xs" sx={{ color: COLORS.textMute, mt: 0.25 }}>
          {hint}
        </Typography>
      </Box>
      <Switch
        checked={value}
        onChange={(e) => onChange(e.target.checked)}
        sx={{
          "--Switch-trackBackground": "rgba(255,255,255,0.15)",
          "&.Mui-checked": { "--Switch-trackBackground": COLORS.brand },
        }}
      />
    </Stack>
  );
}

function NumberOption({ label, hint, value, onChange, placeholder }) {
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      spacing={2}
      sx={{
        p: 1.5,
        borderRadius: "10px",
        background: value > 0 ? "rgba(99,102,241,0.08)" : "rgba(255,255,255,0.02)",
        border: `1px solid ${value > 0 ? "rgba(99,102,241,0.3)" : "rgba(255,255,255,0.06)"}`,
        transition: "all 0.2s",
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0, pr: 1 }}>
        <Typography level="body-sm" sx={{ color: "#fff", fontWeight: 600 }}>
          {label}
        </Typography>
        <Typography level="body-xs" sx={{ color: COLORS.textMute, mt: 0.25 }}>
          {hint}
        </Typography>
      </Box>
      <Input
        type="number"
        value={value || ""}
        onChange={(e) => onChange(Math.max(0, parseInt(e.target.value) || 0))}
        placeholder={placeholder}
        sx={{
          width: 90,
          flexShrink: 0,
          background: "rgba(255,255,255,0.06)",
          borderColor: "rgba(255,255,255,0.14)",
          color: "#fff",
          fontFamily: "monospace",
          "&::before": { display: "none" },
        }}
      />
    </Stack>
  );
}